/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SipProfileFormView.tsx
 🗓️ Created: 2025-01-08
 🧩 Description:
    Form view for creating and editing SIP Profiles with dynamic
    settings organized by categories in tabs with 4-column grid layout.

 🔧 Notes:
    - Handles profile creation/edit with tabbed interface
    - Settings grouped by categories (Transport, TLS, DTMF, etc.)
    - System profiles have restricted editing
    - Supports reset to default values
    - 4-column grid layout matching Users form standard

 🔁 Last Updated: 2025-01-08 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Settings as SettingsIcon, Search, RotateCcw } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuthStore } from '../../store/auth';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { CodecField } from '../../components/ui/CodecField';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { MODULE_SLUGS } from '../../hooks/usePermissions';
import { getParameterConfig, getParameterLabel, getParameterTooltip } from './sipParametersConfig';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import {
  fetchSipProfile,
  updateSipProfile,
  fetchCategoriesList,
  fetchQuickList,
} from './api';
import { SipProfile, SipProfileSetting } from './types';
import { SipProfileSettingsTabs } from './components/SipProfileSettingsTabs';

interface Category {
  id: number;
  name: string;
  description?: string;
}

type Setting = SipProfileSetting;

export default function SipProfileFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || t('sipProfiles.defaultTenant');

  const [profile, setProfile] = useState<SipProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(null); // Will be set to first category ID
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Record<number, number>>({}); // Count of results per category

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<any[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  // Permission management
  const { canEdit } = useFormPermissions(MODULE_SLUGS.SIP_SETTINGS);

  const [formData, setFormData] = useState<{
    id?: string | number;
    name: string;
    description: string;
    enabled: boolean;
    settings: Setting[];
  }>({
    id: profile?.id,
    name: profile?.name || '',
    description: profile?.description || '',
    enabled: profile?.enabled !== undefined ? profile.enabled : true,
    settings: [] as Setting[]
  });

  const showErrorRef = useRef(showError);
  const navigateRef = useRef(navigate);
  const translateRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  // Load profile data from API
  useEffect(() => {
    if (!id) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadProfile = async () => {
      try {
        const result = await fetchSipProfile(id!);

        if (isMounted) {
          setProfile(result);
          setFormData({
            id: result.id,
            name: result.name || '',
            description: result.description || '',
            enabled: result.enabled !== undefined ? result.enabled : true,
            settings: Array.isArray(result.settings) ? result.settings : [],
          });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          showErrorRef.current?.(translateRef.current('sipProfiles.errors.errorLoadingProfile'));
          navigateRef.current('/settings/technology/sip');
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, tenantSlug, user?.id, i18n.language]);

  // Translate category name - Direct mapping for reliability
  const getCategoryName = (categoryName: string): string => {
    const lang = i18n.language as 'en' | 'es';

    // Map old names to new shorter names
    const nameMapping: Record<string, string> = {
      'General': 'General Settings',
      'Transport': 'Transport (SIP/TLS)',
      'Media (RTP & Codecs)': 'Media & Codecs',
      'NAT & ACL': 'NAT / ACL',
      'Authentication & Security': 'Security & Auth',
      'TLS & Security': 'Security & Auth',
      'Registration & Presence': 'Registration',
      'Call Behavior & Routing': 'Call Handling',
      'SIP Logic & Negotiation': 'Call Handling',
      'Advanced & Monitoring': 'Advanced',
      'Advanced & Diagnostics': 'Advanced'
    };

    // Get the new name
    const newName = nameMapping[categoryName] || categoryName;

    // Direct translation mapping for new names
    const translations: Record<string, Record<'en' | 'es', string>> = {
      'General Settings': { en: 'General Settings', es: 'Configuración General' },
      'Transport (SIP/TLS)': { en: 'Transport (SIP/TLS)', es: 'Transporte (SIP/TLS)' },
      'Media & Codecs': { en: 'Media & Codecs', es: 'Media y Códecs' },
      'NAT / ACL': { en: 'NAT / ACL', es: 'NAT / ACL' },
      'Security & Auth': { en: 'Security & Auth', es: 'Seguridad y Auth' },
      'Registration': { en: 'Registration', es: 'Registro' },
      'Call Handling': { en: 'Call Handling', es: 'Manejo de Llamadas' },
      'Advanced': { en: 'Advanced', es: 'Avanzado' }
    };

    return translations[newName]?.[lang] || newName;
  };

  // Filter settings by search query
  const filterSettings = (settings: Setting[]): Setting[] => {
    if (!searchQuery.trim()) {
      return settings;
    }

    const query = searchQuery.toLowerCase();
    return settings.filter(setting => {
      const name = setting.name?.toLowerCase() || '';
      const value = setting.value?.toString().toLowerCase() || '';
      const label = getParameterLabel(setting.name, t)?.toLowerCase() || '';
      const tooltip = getParameterTooltip(setting.name, t)?.toLowerCase() || '';

      return name.includes(query) ||
        value.includes(query) ||
        label.includes(query) ||
        tooltip.includes(query);
    });
  };

  // Get filtered categories (only show categories with matching settings)
  // Memoize filtered categories to avoid recalculation
  const filteredCategories = useMemo((): Category[] => {
    // Always filter out categories with no settings
    return categories.filter(category => {
      const categorySettings = formData.settings.filter(s =>
        s.categoryId === category.id || s.category === category.name
      );

      // If there's a search query, apply additional filtering
      if (searchQuery.trim()) {
        const filteredSettings = filterSettings(categorySettings);
        return filteredSettings.length > 0;
      }

      // Without search, just check if category has any settings
      return categorySettings.length > 0;
    });
  }, [categories, formData.settings, searchQuery]);

  // Set first category as active tab when categories load
  useEffect(() => {
    if (categories.length > 0 && activeTab === null) {
      // Get first category with settings
      if (filteredCategories.length > 0) {
        setActiveTab(String(filteredCategories[0].id));
      }
    }
  }, [categories, activeTab, filteredCategories]);

  // Ensure active tab is valid after filtering (only show tabs with settings)
  useEffect(() => {
    if (activeTab !== null && categories.length > 0 && formData.settings.length > 0) {
      // Check if current activeTab still has settings
      const isActiveTabValid = filteredCategories.some(cat => String(cat.id) === activeTab);

      // If current tab is empty, switch to first tab with settings
      if (!isActiveTabValid && filteredCategories.length > 0) {
        setActiveTab(String(filteredCategories[0].id));
      }
    }
  }, [activeTab, filteredCategories]);

  // Smart search: Calculate results per category
  useEffect(() => {
    if (!searchQuery.trim() || categories.length === 0) {
      setSearchResults({});
      return;
    }

    const query = searchQuery.toLowerCase();
    const results: Record<number, number> = {};

    // Count matches per category
    categories.forEach((category) => {
      const categorySettings = formData.settings.filter(s =>
        s.categoryId === category.id || s.category === category.name
      );

      const matchCount = categorySettings.filter((setting) => {
        const name = setting.name?.toLowerCase() || '';
        const value = setting.value?.toString().toLowerCase() || '';
        const label = getParameterLabel(setting.name, t)?.toLowerCase() || '';
        const tooltip = getParameterTooltip(setting.name, t)?.toLowerCase() || '';

        return (
          name.includes(query) ||
          value.includes(query) ||
          label.includes(query) ||
          tooltip.includes(query)
        );
      }).length;

      results[category.id] = matchCount;
    });

    setSearchResults(results);

    // Auto-switch to first category with results ONLY when search query changes
    const firstCategoryWithResults = categories.find(cat => results[cat.id] > 0);
    if (firstCategoryWithResults) {
      setActiveTab(String(firstCategoryWithResults.id));
    }
  }, [searchQuery, categories, formData.settings, i18n.language]); // Removed activeTab from dependencies

  // Get settings for a category with filtering
  const getSettingsForCategory = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    const categorySettings = formData.settings
      .filter(setting =>
        setting.categoryId === categoryId ||
        (category && setting.category === category.name)
      )
      .sort((a, b) => (a.settingOrder || 0) - (b.settingOrder || 0));

    return filterSettings(categorySettings);
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadCategories = async () => {
      try {
        const result = await fetchCategoriesList();
        const categoriesData: Category[] = Array.isArray(result?.data) ? result.data : [];

        const categoryOrder = [
          'General Settings',
          'Transport (SIP/TLS)',
          'Media & Codecs',
          'NAT / ACL',
          'Security & Auth',
          'Registration',
          'Call Handling',
          'Advanced'
        ];

        const sortedCategories = categoriesData.sort((a, b) => {
          const indexA = categoryOrder.indexOf(a.name);
          const indexB = categoryOrder.indexOf(b.name);

          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return 0;
        });

        setCategories(sortedCategories);
      } catch (error) {
        if (!controller.signal.aborted) {
          showErrorRef.current?.(translateRef.current('sipProfiles.errors.errorLoadingCategories'));
        }
      }
    };

    loadCategories();

    return () => {
      controller.abort();
    };
  }, [tenantSlug, user?.id, i18n.language]);


  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchQuickList();
      setQuickListProfiles(result.data || []);
    } catch (error) {
      showErrorRef.current?.(translateRef.current('sipProfiles.errors.errorLoadingQuickList'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length, loadQuickList]);

  // Client-side filtering
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter((profile) =>
      profile.name?.toLowerCase().includes(query) ||
      (profile.description || '').toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  // Handle quick list selection
  const handleQuickListSelect = useCallback(async (selectedProfile: SipProfile) => {
    setShowQuickList(false);
    setQuickListSearch('');

    try {
      // Load the complete profile data including settings
      const result = await fetchSipProfile(selectedProfile.id);

      setFormData({
        id: result.id,
        name: result.name,
        description: result.description || '',
        enabled: result.enabled !== undefined ? result.enabled : true,
        settings: result.settings || [],
      });
    } catch (error) {
      showErrorRef.current?.(translateRef.current('sipProfiles.errors.errorLoadingProfile'));
      // Fallback to basic data
      setFormData({
        id: selectedProfile.id,
        name: selectedProfile.name,
        description: selectedProfile.description || '',
        enabled: selectedProfile.enabled !== undefined ? selectedProfile.enabled : true,
        settings: [],
      });
    }
  }, []);

  const handleInputChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSettingChange = (settingIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      settings: prev.settings.map((setting, index) =>
        index === settingIndex ? { ...setting, value } : setting
      )
    }));
  };

  const handleResetToDefault = (settingIndex: number) => {
    const setting = formData.settings[settingIndex];
    handleSettingChange(settingIndex, setting.defaultValue);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = i18n.language === 'es' ? 'El nombre del perfil es requerido' : 'Profile name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError(t('common.fixErrors'));
      return;
    }

    setSaving(true);

    try {
      // Ensure settings is always an array and set context automatically
      const settings = Array.isArray(formData.settings) ? formData.settings : [];

      // Auto-set context based on profile name
      const contextValue = formData.name?.toLowerCase().includes('internal') ? 'local' : 'public';

      // Update or add context setting
      const contextSettingIndex = settings.findIndex(s => s.name === 'context');
      if (contextSettingIndex >= 0) {
        settings[contextSettingIndex].value = contextValue;
      } else {
        settings.push({
          name: 'context',
          value: contextValue,
          defaultValue: contextValue,
          settingType: 'param',
          category: 'Call Handling',
          subcategory: 'general',
          enabled: true,
        });
      }

      const dataToSend = {
        name: formData.name,
        description: formData.description || null,
        enabled: formData.enabled,
        settings: settings,
      };

      await updateSipProfile(profile!.id, dataToSend);

      showSuccess(t('sipProfiles.updateSuccess'));

      // Stay on the same page after save (no navigation)
    } catch (error) {
      showError(error.message || t('sipProfiles.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const isReadonly = !canEdit;

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<SettingsIcon className="h-5 w-5 text-primary" />}
        title={`${t('sipProfiles.editProfile')}: ${formData.name}`}
        backButtonText={t('common.list')}
        onBack={() => navigate('/settings/technology/sip')}
        searchInput={formData.settings.length > 0 ? (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('sipProfiles.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <SimpleTooltip
                content={t('common.clear')}
                position="top"
              >
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </SimpleTooltip>
            )}
          </div>
        ) : undefined}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('sipProfiles.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            }
          }
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Information */}
          <div className="bg-card p-3 sm:p-6 rounded-lg border border-border">
            {/* Responsive Grid Layout: 4 cols on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {/* Row 1 */}
              <SimpleTooltip
                content={t('sipProfiles.tooltips.description')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('sipProfiles.description')}
                </label>
              </SimpleTooltip>
              <input
                type="text"
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                disabled={isReadonly}
                className={`px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${isReadonly ? 'opacity-70 cursor-not-allowed' : 'border-border'
                  }`}
                placeholder={t('sipProfiles.enterDescription')}
              />
              <SimpleTooltip
                content={t('sipProfiles.tooltips.enabled')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground cursor-pointer">
                  {t('sipProfiles.enabled')}
                </label>
              </SimpleTooltip>
              <div className="flex justify-start">
                <Toggle
                  value={formData.enabled}
                  onChange={(value) => handleInputChange('enabled', value)}
                  disabled={isReadonly}
                />
              </div>
            </div>
          </div>

          {/* Settings Tabs */}
          <SipProfileSettingsTabs
            settings={formData.settings}
            filteredCategories={filteredCategories}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            searchResults={searchResults}
            searchQuery={searchQuery}
            isReadonly={isReadonly}
            getCategoryName={getCategoryName}
            onSettingChange={handleSettingChange}
            onResetToDefault={handleResetToDefault}
            t={t}
          />
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSave={() => handleSubmit(new Event('submit') as any)}
        onCancel={() => {
          if (profile) {
            setFormData({
              id: profile.id,
              name: profile.name || '',
              description: profile.description || '',
              enabled: profile.enabled !== undefined ? profile.enabled : true,
              settings: Array.isArray(profile.settings) ? profile.settings : [],
            });
            setErrors({});
          }
        }}
        saveText={saving ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
        saveDisabled={saving || !canEdit}
        isLoading={saving}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('sipProfiles.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect(profile)}
        currentItemId={id}
        renderItem={(profile, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              handleQuickListSelect(profile);
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{profile.name}</div>
            {profile.description && (
              <div className="text-sm text-muted-foreground mt-1">
                {profile.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {t(`sipProfiles.quickListStatus.${profile.enabled ? 'enabled' : 'disabled'}`)} • {t('sipProfiles.quickListSettingsCount', {
                count: profile.settings_count || 0
              })}
            </div>
          </button>
        )}
        searchPlaceholder={t('sipProfiles.quickListSearchPlaceholder')}
        emptyMessage={t('sipProfiles.quickListEmpty.default')}
        noResultsMessage={t('sipProfiles.quickListEmpty.search')}
        headerIcon={<SettingsIcon className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

